<template>
  <div class="vp-root">
    <DxDataGrid class="salarii-grid" :data-source="randuriFiltrate" key-expr="ID" height="100%"
      :remote-operations="false" :show-row-lines="true" :hover-state-enabled="true" :show-borders="true"
      :onCellPrepared="onCellPrepared" @row-updating="onRowUpdating">
      <DxEditing :allow-updating="true" mode="cell" :select-text-on-edit-start="true" />
      <DxSelection mode="single" />
      <DxFilterRow :visible="true" />
      <DxPaging :page-size="20" />
      <DxLoadPanel :enabled="true" />
      <DxSorting mode="multiple" />
      <DxColumn data-field="Cod" caption="Cod" :allow-editing="false" />
      <DxColumn data-field="Nume" caption="Denumire" :allow-editing="false" />
      <DxColumn data-field="Valoare" caption="Valoare" data-type="number" />
    </DxDataGrid>
  </div>
</template>

<script lang="ts">
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxLoadPanel,
  DxPaging,
  DxSelection,
  DxSorting,
  DxEditing,
} from "devextreme-vue/data-grid";
import { defineComponent } from "vue";
import ValoriParametriSalarizare from "@/services/PER/ValoriParametriSalarizare";
import notify from "devextreme/ui/notify";

export default defineComponent({
  components: {
    DxColumn,
    DxDataGrid,
    DxFilterRow,
    DxLoadPanel,
    DxPaging,
    DxSelection,
    DxSorting,
    DxEditing,
  },
  props: {
    empId: {
      type: String,
      required: false,
    },
    mode: {
      type: String,
      required: false,
      default: "Angajati",
    },
    luna: { type: Number, required: false },
    anul: { type: Number, required: false },
    reloadTick: { type: Number, default: 0 },
    // Filtru tri-state „Vezi datele cu preluare automata" (ca legacy):
    //  false = doar manuale (implicit) · true = doar automate · null = toate.
    veziAutomat: { type: Boolean as unknown as () => boolean | null, required: false, default: false },
  },
  computed: {
    randuriFiltrate(): any[] {
      const rows = this.dsValoriParametriiSalarizare;
      if (this.veziAutomat === null || this.veziAutomat === undefined) return rows;
      return rows.filter((r: any) => !!r.Automat === this.veziAutomat);
    },
  },
  data() {
    return {
      dsValoriParametriiSalarizare: [] as any[],
    };
  },
  watch: {
    empId: {
      immediate: true,
      handler(newVal) {
        this.reload(newVal, this.mode);
      },
    },
    mode(newVal) {
      this.reload(this.empId, newVal);
    },
    luna() {
      this.reload(this.empId, this.mode);
    },
    anul() {
      this.reload(this.empId, this.mode);
    },
    reloadTick() {
      this.reload(this.empId, this.mode);
    },
  },
  methods: {
    async reload(empId?: string | null, mode?: string) {
      const luna = this.luna ?? new Date().getMonth() + 1;
      const anul = this.anul ?? new Date().getFullYear();
      // Companie și „Valori implicite" se aplică tuturor (nu depind de un angajat).
      // Doar modul „Angajati" are nevoie de un angajat selectat.
      if (mode === "Angajati" && !empId) {
        this.dsValoriParametriiSalarizare = [];
        return;
      }

      try {
        let resp;
        if (mode === "Companie") {
          resp = await ValoriParametriSalarizare.GetValoriParametriSalarizareCompanie(luna, anul);
        } else if (mode === "Valori implicite") {
          resp = await ValoriParametriSalarizare.GetValoriParametriSalarizareImplicite(luna, anul);
        } else {
          resp = await ValoriParametriSalarizare.GetValoriParametriSalarizareAngajat(empId, luna, anul);
        }

        const data = resp?.data || [];
        const arr = data.map((x: any, idx: number) => ({
          ID: x.ID ?? x.Cod ?? idx + 1,
          ...x,
        }));
        this.dsValoriParametriiSalarizare = arr;
      } catch (err) {
        console.error(err);
        this.dsValoriParametriiSalarizare = [];
      }
    },
    onRowUpdating(e: any) {
      if (!("Valoare" in (e.newData || {}))) return;
      const cod = e.oldData?.Cod;
      const nouaVal = e.newData.Valoare;
      const valoare = nouaVal === "" || nouaVal === undefined ? null : Number(nouaVal);
      const payload = {
        Mode: this.mode || "Companie",
        EmpId: this.mode === "Angajati" ? (this.empId ?? null) : null,
        Cod: cod,
        Valoare: valoare,
        Luna: this.luna ?? new Date().getMonth() + 1,
        Anul: this.anul ?? new Date().getFullYear(),
      };
      ValoriParametriSalarizare.SaveIntroducere(payload)
        .catch((err: any) => {
          console.error(err);
          notify(err?.response?.data?.message || "Eroare la salvare!", "error", 4000);
        });
    },
     onCellPrepared(e) {
    // Valoarea inputurilor automate (cu preluare) e gri, ca în legacy (CustomDrawCell).
    if (e.rowType === "data" && e.column?.dataField === "Valoare" && e.data?.Automat) {
      e.cellElement.style.backgroundColor = "#c0c0c0";
    }
  },
  },
});
</script>
<style scoped>
.vp-root {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}
.salarii-grid {
  flex: 1;
  min-height: 0;
  width: 100%;
  border: 1px solid #9db0d4;
  box-sizing: border-box;
}

::v-deep .salarii-grid .dx-datagrid-rowsview .dx-row>td {
  height: 10px;
  padding: 8px 6px;
}

/* header albastru */
.salarii-grid .dx-datagrid-headers {
  background: #3b5485;
  color: #fff;
  font-weight: 600;
  border-bottom: 1px solid #2f446a;
}

/* alternanta */
.salarii-grid .dx-row-alt {
  background: #f6f8fb;
}

/* rand selectat */
.salarii-grid .dx-datagrid-rowsview .dx-selection,
.salarii-grid .dx-datagrid-rowsview .dx-row-focused {
  background: #27406b !important;
  color: #fff;
}

/* rand filtru */
.salarii-grid .dx-datagrid-filter-row td {
  background: #dfe4ee;
  border-bottom: 1px solid #c6cfde;
}

.salarii-grid .dx-datagrid-filter-row .dx-editor-with-menu .dx-texteditor-input {
  height: 26px;
  font-size: 12px;
}

/* celule compacte */
.salarii-grid .dx-data-row td {
  padding: 4px 6px;
  font-size: 13px;
}

/* iconite albe in header */
.salarii-grid .dx-datagrid-headers .dx-sort,
.salarii-grid .dx-datagrid-headers .dx-header-filter {
  color: #fff;
}

</style>
